import type { GraphQLObject, GraphQLVariables, QueryResult, QueryStore } from '$houdini';
import { derived, writable } from 'svelte/store';

/**
 * Returns an improved version of the houdini store with the following capabilities:
 * - reset capability to be able to completely reset the store and its data
 * - keep a reference to the last fetch promise to be able to wait for the fetch to complete when
 *   needed
 * - centralized error handling that includes both graphql errors and fetch errors
 * - derive the data store without the need to manually derive it
 * - onSuccess callback to handle successful fetch operations
 * - initial data support to pre-populate the store with data
 */
export function augmentHoudiniStore<Data extends GraphQLObject, Input extends GraphQLVariables>(
	store: QueryStore<Data, Input>,
	initialData?: Data,
) {
	const isFetchError = writable(false);
	const isReset = writable(false);
	const hasUsedInitialData = writable(false);

	// Keep a reference to the last fetch promise and itinitialize to a resolved promise so the
	// consumer thinks the fecth is done by default
	let promiseRef = new Promise(() => {});
	let onSuccessCallback: ((result: QueryResult<Data, Input>) => void) | null = null;

	const reset = () => {
		isReset.set(true);
	};

	const onSuccess = (callback: (result: QueryResult<Data, Input>) => void) => {
		onSuccessCallback = callback;
	};

	const fetch: typeof store.fetch = async (options) => {
		isFetchError.set(false);

		return new Promise((resolve, reject) => {
			promiseRef = store
				.fetch(options)
				.then((result) => {
					isFetchError.set(false);
					// Mark that initial data has been used since we now have real data
					hasUsedInitialData.set(true);
					// Call onSuccess callback if registered
					if (onSuccessCallback) {
						onSuccessCallback(result);
					}
					resolve(result);
				})
				.catch((err) => {
					isFetchError.set(true);
					reject(err);
				})
				.finally(() => {
					isReset.set(false);
				});
		});
	};

	const lastFetchPromise = () => promiseRef;

	const getAugmentedStore = <T>(derivedFunction: (store: QueryResult<Data, Input>) => T) => ({
		...derived([store, isReset, isFetchError, hasUsedInitialData], ([$store, $isReset, $isFetchError, $hasUsedInitialData]) => {
			if ($isReset) {
				return derivedFunction({
					...$store,
					data: null,
					errors: null,
					fetching: $store.fetching,
				});
			}

			const newStore = {
				...$store,
				// Use initial data only if store data is null/undefined, initial data is available, and it hasn't been used yet
				data: $store.data ?? (!$hasUsedInitialData && initialData ? initialData : null),
				// Move the fetch error in the errors array as we treat fecth errors the same way as graphql
				// errors
				errors: $isFetchError ? [{ message: 'Fetch error' }] : $store.errors,
				fetching: initialData && !$hasUsedInitialData ? false : $store.fetching && !$isFetchError,
			};

			return derivedFunction(newStore);
		}),
		reset,
		fetch,
		lastFetchPromise,
		onSuccess,
	});

	const derive = <T>(fn: (store: QueryResult<Data, Input>) => T) => getAugmentedStore(fn);

	return {
		...getAugmentedStore(store => store),
		derive,
	};
}

export type AugmentedHoudiniStore<T extends QueryStore<any, any>> =
	T extends QueryStore<infer Data, infer Input>
		? ReturnType<typeof augmentHoudiniStore<Data, Input>>
		: never;
